import React, {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";

import useDebounce from "../hooks/useDebounce";
import { useCoverageSummary } from "../hooks/useCoverageSummary";
import { coverageTypeToLabel } from "../utils";
import { isDocumentLibraryCoverageType } from "../coverage-type";
import { filterDocuments as filterDocumentsFn } from "../utils/filterDocuments";

import type { Option } from "../components/SelectFilter";
import type { DocumentCardProps } from "../types";

export type DocumentFilters = {
  search: string;
  searchTerm: string;
  status: Option[];
  coverage: Option[];
  documentType: Option[];
};

const initialFilters: DocumentFilters = {
  search: "",
  searchTerm: "",
  status: [],
  coverage: [],
  documentType: [],
};

export type UseDocumentFiltersReturn = {
  filters: DocumentFilters;
  coverageOptions: Option[];
  updateFilter: <K extends keyof DocumentFilters>(
    key: K,
    value: DocumentFilters[K],
  ) => void;
  updateSearchTerm: (value: string) => void;
  clearFilters: () => void;
  activeFiltersCount: number;
  filterDocuments: (
    documents: DocumentCardProps[],
    coverageName?: string,
  ) => DocumentCardProps[];
  isSearchActive: boolean;
  hasSearchResults: (
    documents: DocumentCardProps[],
    coverageName?: string,
  ) => boolean;
};

export const DocumentFiltersContext =
  createContext<UseDocumentFiltersReturn | null>(null);

function mapCoverageTypeToOption(
  coverageType: string,
  disabledCoverageTypes: ReadonlySet<string>,
): Option {
  if (coverageType === "other") {
    return {
      label: "Other",
      value: coverageType,
      disable: disabledCoverageTypes.has(coverageType),
    };
  }

  if (isDocumentLibraryCoverageType(coverageType)) {
    return {
      label: coverageTypeToLabel(coverageType),
      value: coverageType,
      disable: disabledCoverageTypes.has(coverageType),
    };
  }

  return {
    label: coverageType,
    value: coverageType,
    disable: disabledCoverageTypes.has(coverageType),
  };
}

type DocumentFiltersProviderProps = {
  children: ReactNode;
};

export const DocumentFiltersProvider = ({
  children,
}: DocumentFiltersProviderProps): React.JSX.Element => {
  const coverageSummary = useCoverageSummary();
  const [filters, setFilters] = useState<DocumentFilters>(initialFilters);

  const excludedCoverageTypes = useMemo(
    () => new Set(coverageSummary.excludedCoverages),
    [coverageSummary.excludedCoverages],
  );

  const coverageTypes = useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          coverageSummary.coverages.map((coverage) => coverage.coverageType),
        ),
      ),
    [coverageSummary.coverages],
  );

  const coverageOptions = useMemo(
    () =>
      coverageTypes.map((coverageType) =>
        mapCoverageTypeToOption(coverageType, excludedCoverageTypes),
      ),
    [coverageTypes, excludedCoverageTypes],
  );

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);

  // Update search filter when debounced value changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearchTerm.length >= 3 ? debouncedSearchTerm : "",
    }));
  }, [debouncedSearchTerm]);

  const updateSearchTerm = useCallback((value: string): void => {
    setFilters((prev) => ({ ...prev, searchTerm: value }));
  }, []);

  const updateFilter = useCallback(
    <K extends keyof DocumentFilters>(
      key: K,
      value: DocumentFilters[K],
    ): void => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      coverage: prev.coverage.filter((option) =>
        coverageTypes.includes(option.value),
      ),
    }));
  }, [coverageTypes]);

  const clearFilters = useCallback((): void => {
    setFilters(initialFilters);
  }, []);

  const activeFiltersCount = useMemo<number>(
    () =>
      (filters.search.length >= 3 ? 1 : 0) +
      filters.status.length +
      filters.coverage.length +
      filters.documentType.length,
    [filters.search, filters.status, filters.coverage, filters.documentType],
  );

  const isSearchActive = useMemo<boolean>(
    () => filters.search.length >= 3,
    [filters.search],
  );

  const filterDocuments = useCallback(
    (
      documents: DocumentCardProps[],
      coverageName?: string,
    ): DocumentCardProps[] =>
      filterDocumentsFn({
        documents,
        search: filters.search,
        statusFilters: filters.status,
        documentTypeFilters: filters.documentType,
        coverageName,
      }),
    [filters.status, filters.documentType, filters.search],
  );

  const hasSearchResults = useCallback(
    (documents: DocumentCardProps[], coverageName?: string): boolean => {
      if (!isSearchActive) return true;
      return filterDocuments(documents, coverageName).length > 0;
    },
    [isSearchActive, filterDocuments],
  );

  const value = useMemo(
    () => ({
      filters,
      coverageOptions,
      updateFilter,
      updateSearchTerm,
      clearFilters,
      activeFiltersCount,
      filterDocuments,
      isSearchActive,
      hasSearchResults,
    }),
    [
      filters,
      coverageOptions,
      updateFilter,
      updateSearchTerm,
      clearFilters,
      activeFiltersCount,
      filterDocuments,
      isSearchActive,
      hasSearchResults,
    ],
  );

  return (
    <DocumentFiltersContext.Provider value={value}>
      {children}
    </DocumentFiltersContext.Provider>
  );
};
